import { useCallback, useEffect, useState } from 'react';

import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';

import { CruisesContract as API } from '@luxuryescapes/contract-svc-cruise';

import dealCrawlerService, { CruiseDealCrawler } from '~/services/cruises/DealCrawlerService';

import { parseSearchString } from '~/utils/url';

interface Filter {
  sourceUrl?: string;
}

interface CruisesData {
  dealCrawlers: Array<CruiseDealCrawler>;
  page: number;
  total: number;
  isLoading: boolean;
  recordsPerPage: number;
  paginate: (page: number) => void;
  setFilter: (value: Filter) => void;
  filter: Filter;
  getDealCrawlers: (params?: API.PaginationParams) => Promise<void>;
}

const RECORDS_PER_PAGE = 15;

export default function useCruiseDealsCrawler(): CruisesData {
  const [filter, setFilter] = useState<Filter>({});
  const { search } = useLocation();
  const { push: setQueryString } = useHistory();
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [dealCrawlers, setDealCrawlers] = useState<Array<CruiseDealCrawler>>([]);
  const [isLoading, setIsLoading] = useState(false);

  const getDealCrawlers = useCallback(
    async (params?: API.PaginationParams) => {
      try {
        const recordsPerPage = params?.take || RECORDS_PER_PAGE;
        const skip = (page - 1) * recordsPerPage;
        setIsLoading(true);
        const res = await dealCrawlerService.listWithPagination({
          skip,
          take: recordsPerPage,
          ...filter,
        });
        setDealCrawlers(res.result);
        setTotal(res.total);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    },
    [page, filter],
  );

  const setCurrentPage = useCallback((): void => {
    const queryString = parseSearchString(search);
    if (queryString) {
      const pageToGo = parseInt(queryString.page as string);
      if (pageToGo) {
        setPage(pageToGo);
      }
    }
  }, [search]);

  const paginate = useCallback(
    (page: number): void => {
      const queryString = new URLSearchParams({
        page: page.toString(),
      }).toString();
      setQueryString({ search: queryString });
      setPage(page);
    },
    [setQueryString],
  );

  useEffect(() => {
    setCurrentPage();
  }, [setCurrentPage]);

  useEffect(() => {
    getDealCrawlers();
  }, [getDealCrawlers]);

  return {
    filter,
    setFilter,
    dealCrawlers,
    getDealCrawlers,
    page,
    recordsPerPage: RECORDS_PER_PAGE,
    paginate,
    total,
    isLoading,
  };
}
